import React, { useEffect, useState } from 'react';
import {
  FileSignature, Receipt, Newspaper, Calendar, Sparkles,
  Loader2, Crown, DollarSign, Scale, Megaphone, ChevronRight,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const PERSONA_ICON = {
  maryam: Scale,
  khadija: DollarSign,
  yusuf: Crown,
  fatima: Megaphone,
};

export default function AIDrafts() {
  /* eslint-disable -- v6 react-hooks/immutability + set-state-in-effect false-positives on the load+draft effects */
  const [drafts, setDrafts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftingTab, setDraftingTab] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ai/agents/drafts');
      setDrafts(data);
    } catch (e) {
      toast.error('Could not load drafts.');
    } finally { setLoading(false); }
  };

  const draftBoardBrief = async () => {
    setDraftingTab('brief');
    try {
      const { data } = await api.post('/ai/agents/yusuf/draft-board-brief', { period_days: 7 });
      toast.success(`Yusuf composed a board brief: "${data.headline?.slice(0, 60) || 'untitled'}"`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Brief failed.'); }
    finally { setDraftingTab(null); }
  };

  if (loading) return <div className="ve-section text-sm text-slate-500">Loading drafts…</div>;

  const sections = [
    { key: 'board_briefs',   icon: Calendar,       label: 'Board Briefs',     persona: 'yusuf' },
    { key: 'agreements',     icon: FileSignature,  label: 'Agreement Drafts', persona: 'maryam' },
    { key: 'invoices',       icon: Receipt,        label: 'Invoice Drafts',   persona: 'khadija' },
    { key: 'press_releases', icon: Newspaper,      label: 'Press Releases',   persona: 'fatima' },
  ];

  return (
    <div className="ve-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <Sparkles size={14} className="text-[#8B3FC5]"/>
            AI Drafts
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Your team&rsquo;s <span className="ve-gold-text">draft outputs.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Maryam drafts agreements. Khadija drafts invoices. Yusuf composes board briefs every
            Monday at 07:00. Fatima drafts press releases. Every draft is saved with status =
            <span className="font-mono">draft</span> — promote them through the regular workflow.
          </p>
        </div>
        <button
          onClick={draftBoardBrief}
          disabled={draftingTab === 'brief'}
          data-testid="drafts-yusuf-brief-now"
          className="ve-btn-primary text-xs self-start disabled:opacity-50"
        >
          {draftingTab === 'brief' ? <Loader2 size={12} className="animate-spin"/> : <Crown size={12}/>}
          Ask Yusuf for a brief now
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sections.map(({ key, icon: Icon, label, persona }) => {
          const items = drafts?.[key] || [];
          const PIcon = PERSONA_ICON[persona] || Sparkles;
          return (
            <div key={key} className="ve-card p-5" data-testid={`drafts-section-${key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon size={18} className="text-[#5E1F8B]"/>
                  <h2 className="display font-bold">{label}</h2>
                </div>
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                  by <PIcon size={11}/> {persona}
                </div>
              </div>
              {items.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-4">No drafts yet.</div>
              ) : (
                <div className="space-y-2">
                  {items.slice(0, 6).map((d) => {
                    const id = d.agreement_id || d.invoice_id || d.brief_id || d.press_release_id;
                    return (
                      <div key={id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`draft-row-${id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate">
                            {d.title || d.headline || d.partnership_type || d.counterparty_name || '—'}
                          </div>
                          <div className="text-[10px] font-mono text-slate-400 truncate">{id}</div>
                        </div>
                        <ChevronRight size={14} className="text-slate-400 shrink-0"/>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
